import React, { useState, useEffect } from "react";
import { get } from "lodash";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Map } from "immutable";
import api from "APP_ROOT/api";
import { Modal, Header, Close } from "MODULES/shared/components/Modal";
import {
  setInvoiceInitialValues,
  addLineItemsFunc,
  showFormFunc,
  SHIPPING_CHECKLIST,
  COMMERCIAL_INVOICE,
  SEND_WEIGHTS_AND_DIMS,
  COMMERCIAL_INVOICE_LINE_ITEMS,
  DOWNLOAD_PACKING_SLIP
} from "MODULES/shared/components/CommercialInvoice/helpers";
import LineItemsForm from "MODULES/shared/components/CommercialInvoice/Form/FormSections/LineItemsForm";
import CommercialInvoiceForm from "MODULES/shared/components/CommercialInvoice/Form";
import CommercialDiscreetButton from "MODULES/shared/components/CommercialInvoice/Form/CommercialDiscreetButton";
import {
  COUNTRY_OF_MFG,
  HEIGHT,
  LENGTH,
  NET_HEIGHT,
  NET_LENGTH,
  NET_WEIGHT,
  NET_WEIGHT_OZ,
  NET_WEIGHT_UNIT,
  NET_WIDTH,
  WEIGHT,
  WEIGHT_UNIT,
  WIDTH
} from "MODULES/parts/constants";
import {
  CREATE_COMMERCIAL_INVOICE,
  USER,
  WAREHOUSE_EXPERT,
  WAREHOUSE_LOGISTICS_DIRECTOR
} from "MODULES/users/constants";
import { mapROToForm, mapSOToForm } from "MODULES/SalesOrders/helpers";
import { properCase } from "LIB/formatters/text";
import { PRICE_PER_UNIT } from "MODULES/OutgoingQuotes/constants";
import { ATTACHMENTS, INVENTORY_ID } from "APP_ROOT/constants";
import { MISTAKE_SHIPMENT, SHIPMENT } from "MODULES/Receiving/constants";
import DownloadPackingSlip from "MODULES/Shipping/components/Dashboard/DownloadPackingSlip/DownloadPackingSlip";
import { Col } from "react-bootstrap";
import Body from "MODULES/shared/components/Modal/Body";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import { SPECIAL_INSTRUCTIONS } from "MODULES/SalesOrders/constants";
import { useCurrentUser } from "APP_ROOT/hooks";
import { hasAnyOfSubroles } from "MODULES/users/helpers";
import { Authorized } from "MODULES/shared/components";
import { RETURNED_CONSIGNMENT } from "MODULES/inventories/constants";
import { SHIP_MULTIPLE_ITEM_IDS } from "MODULES/Shipping/constants";
import Form from "./Form";
import SendWeightDimsForm from "./SendWeightDimsForm";
import {
  CONSIGNMENT,
  INVENTORY_TRANSFER_ORDER,
  MRO_ITEM,
  PO_ITEM,
  REJECTED_CORE_ORDER,
  RETURN_TO_CUSTOMER_RECORD,
  RETURNED_CORE,
  RMA,
  RO_ITEM,
  SO_ITEM,
  TRANSFER
} from "../../../../Orders/shared/constants";
import pojo from "../../../../../lib/pojos";
import ShipTo from "./ShipTo";

const mapType = {
  [CONSIGNMENT]: properCase(CONSIGNMENT),
  [RETURNED_CONSIGNMENT]: "ReturnedConsignment",
  [RETURN_TO_CUSTOMER_RECORD]: "ReturnToCustomerRecord",
  [INVENTORY_TRANSFER_ORDER]: "InventoryTransferOrder",
  [REJECTED_CORE_ORDER]: "RejectedCoreOrder",
  [RETURNED_CORE]: "ReturnedCore",
  [RO_ITEM]: "RepairOrderLineItem",
  [RMA]: "VendorRma",
  mistake_shipment: "MistakeShipment",
  [MRO_ITEM]: "ManagedRepairOrderLineItem",
  [SHIPMENT]: "Inventory",
  [SO_ITEM]: "SalesOrderLineItem",
  [PO_ITEM]: "PurchaseOrderLineItem"
};

const downloadMapType = {
  [RO_ITEM]: "repairOrders",
  [SO_ITEM]: "salesOrders",
  [MISTAKE_SHIPMENT]: "mistakeShipments",
  [MRO_ITEM]: "managedRepairOrders",
  [RETURNED_CONSIGNMENT]: "returnedConsignment",
  [PO_ITEM]: "purchaseOrders",
  [REJECTED_CORE_ORDER]: "rejectedCores",
  [RETURN_TO_CUSTOMER_RECORD]: "returnToCustomerRecords",
  [RMA]: "rmas"
};

const quantity = shippingRecord => {
  if (get(shippingRecord, "type") === REJECTED_CORE_ORDER) {
    return 1;
  }
  return (
    shippingRecord && shippingRecord.quantity - shippingRecord.quantity_shipped
  );
};

const salesEA = shippingRecord =>
  parseFloat(get(shippingRecord, PRICE_PER_UNIT, 0));

function Create({ open, close, currentUser, shippingRecord }) {
  const initialValues = {
    read_only_part_number: shippingRecord && shippingRecord.part_number,
    read_only_part_uuid: shippingRecord && shippingRecord.part_uuid,
    read_only_serial_number: shippingRecord && shippingRecord.serial_number,
    read_only_inventory_uuid: shippingRecord && shippingRecord.inventory_uuid,
    read_only_company:
      shippingRecord &&
      shippingRecord.type !== MISTAKE_SHIPMENT &&
      shippingRecord.company_name,
    read_only_company_uuid: shippingRecord && shippingRecord.company_uuid,
    read_only_customer_po:
      shippingRecord && shippingRecord.customer_purchase_order_number,
    read_only_customer_email: shippingRecord && shippingRecord.customer_email,
    read_only_salesperson: shippingRecord && shippingRecord.seller_name,
    read_only_sales_ea: salesEA(shippingRecord),
    outgoing_awb_number: shippingRecord && shippingRecord.outgoing_awb_number,
    hazmat: !!(shippingRecord && shippingRecord.hazmat),
    bio_hazmat: !!(shippingRecord && shippingRecord.bio_hazmat),
    quantity: quantity(shippingRecord),
    discreet: !!(shippingRecord && shippingRecord.discreet),
    customer_to_provide_own_paperwork: !!(
      shippingRecord && shippingRecord.customer_to_provide_own_paperwork
    ),
    reference_record_line_item_id:
      shippingRecord && shippingRecord.type === SHIPMENT
        ? shippingRecord.inventory_id
        : shippingRecord.order_id,
    reference_record_line_item_type:
      shippingRecord && mapType[shippingRecord.type],
    ship_as_multiple: true,
    line_items: [],
    pictures_required: get(shippingRecord, "parent.pictures_required"),
    net_weight_unit: get(shippingRecord, NET_WEIGHT_UNIT) || "lb",
    shipping_method:
      (shippingRecord && shippingRecord.shipping_method) ||
      shippingRecord.ship_to_shipping_method,
    ...pojo(shippingRecord || {}).pick(
      ATTACHMENTS,
      HEIGHT,
      WEIGHT,
      WIDTH,
      LENGTH,
      WEIGHT_UNIT,
      NET_WEIGHT,
      NET_WEIGHT_OZ,
      NET_HEIGHT,
      NET_WIDTH,
      NET_LENGTH,
      COUNTRY_OF_MFG,
      INVENTORY_ID,
      SHIP_MULTIPLE_ITEM_IDS,
      SPECIAL_INSTRUCTIONS
    )
  };

  const user = useCurrentUser();

  const [sendWtDims, setSendWtDims] = useState(false);
  const [weightsAndDims, setWeightsAndDims] = useState({});

  const [currentForm, setCurrentForm] = useState(SHIPPING_CHECKLIST);
  const [salesOrder, setSalesOrder] = useState({ so: [] });
  const [repairOrder, setRepairOrder] = useState({ ro: [] });
  const [loading, setLoading] = useState(true);
  const [lineItems, setLineItems] = useState([]);
  const salesOrderId =
    shippingRecord.type === SO_ITEM && get(shippingRecord, "parent.id")
      ? shippingRecord.parent.id
      : "";

  const repairOrderId =
    shippingRecord.type === RO_ITEM && get(shippingRecord, "parent.id")
      ? shippingRecord.parent.id
      : "";
  const [parentRecord, setParentRecord] = useState(null);
  const [loadingParentRecord, setLoadingParentRecord] = useState(true);
  const [packingUrl, setPackingUrl] = useState(null);

  useEffect(() => {
    if (shippingRecord && shippingRecord.type === SO_ITEM) {
      api.salesOrders.show(salesOrderId).then(resp => {
        setSalesOrder(resp.data);
        const result = resp.data.line_items;
        setLineItems([result[0].inventory]);
        setLoading(false);
      });
    } else if (shippingRecord && shippingRecord.type === RO_ITEM) {
      api.repairOrders.show(repairOrderId).then(resp => {
        setRepairOrder(resp.data);
        const result = resp.data.line_items;
        setLineItems([result[0].inventory]);
        setLoading(false);
      });
    }
    return function cleanup() {};
  }, []);

  useEffect(() => {
    const apiType = downloadMapType[shippingRecord.type];
    if (apiType) {
      const id =
        shippingRecord.parent && RMA !== shippingRecord.type
          ? shippingRecord.parent.id
          : shippingRecord.order_id;
      api[apiType].show(id, "lines_no_details").then(resp => {
        setParentRecord(resp.data);
        setLoadingParentRecord(false);
      });
    } else {
      setLoadingParentRecord(false);
    }

    return function cleanup() {};
  }, []);

  const invoiceInitialValues = setInvoiceInitialValues({
    lineItems,
    shippingRecord,
    salesOrder: mapSOToForm(salesOrder),
    repairOrder: mapROToForm(repairOrder)
  });
  const showForm = showFormFunc({
    salesOrder,
    repairOrder,
    setCurrentForm
  });

  const addLineItems = addLineItemsFunc({
    setLineItems,
    setCurrentForm
  });

  const [shippingAddressWarning, setShippingAddressWarning] = useState(false);

  const checkShipping = () => {
    if (!shippingRecord.company_address) {
      setShippingAddressWarning(true);
    } else {
      showForm();
    }
  };

  return (
    <div>
      <Modal open={open} close={close} dialogClass="modal-lg">
        <Header>
          <CommercialDiscreetButton
            display={
              currentForm === COMMERCIAL_INVOICE &&
              hasAnyOfSubroles(user, [
                CREATE_COMMERCIAL_INVOICE,
                WAREHOUSE_LOGISTICS_DIRECTOR,
                WAREHOUSE_EXPERT
              ])
            }
          />

          <Close close={close} />
          <h4 className="modal-title">
            {currentForm.replace("LineItems", "")}

            {(SO_ITEM === shippingRecord.type ||
              (RO_ITEM === shippingRecord.type && shippingRecord.exporting)) &&
              currentForm === SHIPPING_CHECKLIST && (
                <Authorized
                  subroles={[WAREHOUSE_LOGISTICS_DIRECTOR, WAREHOUSE_EXPERT]}
                  permissions={[CREATE_COMMERCIAL_INVOICE]}
                >
                  <button
                    type="button"
                    className="btn btn-success"
                    style={{ marginLeft: "10px", paddingRight: "5px" }}
                    onClick={e => {
                      e.preventDefault();
                      checkShipping();
                    }}
                    disabled={loading || shippingAddressWarning}
                  >
                    {loading ? "...Loading Invoice" : "Commercial Invoice"}
                  </button>
                </Authorized>
              )}
          </h4>
        </Header>
        {currentForm === SHIPPING_CHECKLIST && (
          <Body>
            <FormRow>
              <Col md={9}>
                <Form
                  close={close}
                  initialValues={initialValues}
                  selectedBuyer={currentUser.toJS()}
                  shippingRecord={shippingRecord}
                  enableReinitialize
                  sendWtDims={sendWtDims}
                  setSendWtDims={setSendWtDims}
                  setWeightsAndDims={setWeightsAndDims}
                  parentRecord={parentRecord}
                  loadingParentRecord={loadingParentRecord}
                  packingUrl={packingUrl}
                  setCurrentForm={setCurrentForm}
                  shippingAddressWarning={shippingAddressWarning}
                  loading={loading}
                  showForm={showForm}
                  setPackingUrl={setPackingUrl}
                />
              </Col>
              {![RETURNED_CORE, TRANSFER].includes(shippingRecord.type) && (
                <Col md={3}>
                  <ShipTo
                    shippingRecord={shippingRecord}
                    parentRecord={parentRecord}
                  />
                </Col>
              )}
            </FormRow>
          </Body>
        )}
        {currentForm === SEND_WEIGHTS_AND_DIMS && (
          <SendWeightDimsForm
            close={close}
            initialValues={Object.assign(initialValues, weightsAndDims)}
            id={shippingRecord.parent.id}
            weightsAndDims={weightsAndDims}
            enableReinitialize
            setCurrentForm={setCurrentForm}
            itemId={shippingRecord && shippingRecord.order_id}
          />
        )}
        {[COMMERCIAL_INVOICE_LINE_ITEMS, COMMERCIAL_INVOICE].includes(
          currentForm
        ) && (
          <div
            style={{
              display: currentForm === COMMERCIAL_INVOICE ? "block" : "none"
            }}
          >
            <CommercialInvoiceForm
              close={close}
              salesOrderId={salesOrderId}
              repairOrderId={repairOrderId}
              setCurrentForm={setCurrentForm}
              initialValues={invoiceInitialValues}
              hazmat={shippingRecord.hazmat}
              discreet={shippingRecord.discreet}
              outbound
              enableReinitialize
            />
          </div>
        )}
        {currentForm === COMMERCIAL_INVOICE_LINE_ITEMS && (
          <LineItemsForm
            close={close}
            addLineItems={addLineItems}
            lineItems={salesOrder.line_items || repairOrder.line_items}
            enableReinitialize
          />
        )}
        {currentForm === DOWNLOAD_PACKING_SLIP && (
          <DownloadPackingSlip
            shippingRecord={shippingRecord}
            record={parentRecord}
            setCurrentForm={setCurrentForm}
            url={packingUrl}
          />
        )}
      </Modal>
    </div>
  );
}

Create.propTypes = {
  open: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  currentUser: PropTypes.instanceOf(Map),
  shippingRecord: PropTypes.shape({})
};

export default connect(state => ({
  currentUser: state.session.get(USER)
}))(Create);



// WEBPACK FOOTER //
// ./app/modules/Shipping/components/Dashboard/Create/Create.jsx